import { useMemo } from 'react'
import { differenceInCalendarDays } from 'date-fns'
import { Subtitle, Title } from '@app/components/core/typography/Title'
import { PageLayout } from '@app/pages/layout/PageLayout'
import Column from '@app/components/layout/Column'
import Row from '@app/components/layout/Row'
import { useTranslations } from '@i18n/useLanguage'
import { Text } from '@app/components/core/typography/Text'
import { useQuery } from '@based/sdk/react'
import useMediaQuery from '@app/util/useMediaQuery'
import { PageBreadcrumbs } from '@app/components/blocks/pageBreadcrumbs/PageBreadcrumbs'
import { TabContent } from './sections/TabContent'
import { RightColumn } from './sections/RightColumn'
import { PageMediaGallery } from '@app/components/blocks/mediaGallery/PageMediaGallery'
import { DataCard } from './sections/DataCard'
import defaultProjectPicture from '@app/assets/defaultProjectPicture.png'
import useTransform from '@app/util/useTransform'
import { ViewPlotModel } from 'functions/app/plot/view'
import { isUuid } from '@app/util/helpers'
import { PageSeo } from '@app/components/core/seo/PageSeo'
import { useParamState } from '@app/util/hooks/useParamState'
import { match } from 'ts-pattern'
import { plotTabs } from './sections/plotTabs'
import { getEntityPageStatus } from '@app/util/getEntityPageStatus'
import { NotFoundPage } from '@app/pages/error/NotFoundPage'
import { buildBreadcrumbListJsonLd } from '@app/util/seo/buildBreadcrumbListJsonLd'
import { shouldNoindexPageTab } from '@app/util/seo/shouldNoindexPageTab'
import { useRootDomain } from '@app/util/hooks/useRootDomain'
import { pages } from '@app/util/links'

export type PlotPageProps = {
  plotId: string
}

export const PlotPage = ({ plotId }: PlotPageProps) => {
  const isMobile = useMediaQuery()
  const translations = useTranslations().app
  const transformStyle = useTransform()

  const queryPayload = useMemo(() => {
    return isUuid(plotId) ? { id: plotId } : { slug: plotId }
  }, [plotId])

  const plotQuery = useQuery('view-plot', queryPayload)
  const plot: ViewPlotModel | null = plotQuery.data

  const daysLeft = useMemo(() => {
    if (!plot?.applicationDeadline) {
      return null
    }
    return differenceInCalendarDays(plot.applicationDeadline, Date.now())
  }, [plot?.applicationDeadline])

  const offeredBy = useMemo(() => {
    if (!plot?.owner?.name) {
      return null
    }
    return `${translations.plotPage.offeredBy} ${plot.owner.name}`
  }, [translations.plotPage.offeredBy, plot?.owner?.name])

  const pageStatus = getEntityPageStatus(plotQuery.loading, plot)

  if (pageStatus === 'not-found') {
    return <NotFoundPage />
  }

  if (pageStatus === 'loading') {
    return (
      <PageLayout gap="2rem">
        <PageSeo />
        <Column
          gap="1.5rem"
          alignItems="stretch"
          style={{ marginBottom: '90vh' }}
        >
          <PageBreadcrumbs
            level2={translations.pageNames.plots}
            level3="Loading..."
          />
        </Column>
      </PageLayout>
    )
  }

  if (isMobile) {
    return (
      <PageLayout gap="0rem">
        <PageHead plot={plot} />
        <Column alignItems="stretch" gap="1.5rem">
          <Column>
            <Column gap="0.5rem">
              <Title.Page styledAsLevel={3}>{plot.name}</Title.Page>
              {offeredBy && <Title.H5>{offeredBy}</Title.H5>}
            </Column>
            {plot.byCrowdbuilding && daysLeft && daysLeft > 0 && (
              <Text.Medium style={{ color: 'var(--palette-red-400)' }}>
                {translations.plotPage.applicationClosesInLabel.replace(
                  '{0}',
                  daysLeft.toString(),
                )}
              </Text.Medium>
            )}
            <Text.Medium>{plot.description}</Text.Medium>
          </Column>
          <PageMediaGallery title={plot.name} images={plot.media} />
          <DataCard plot={plot} />
        </Column>
        <TabContent plot={plot} />
      </PageLayout>
    )
  }

  return (
    <PageLayout gap="2rem">
      <PageHead plot={plot} />
      <Column gap="1.5rem" alignItems="stretch" style={transformStyle}>
        <PageBreadcrumbs
          level2={translations.pageNames.plots}
          level3={plot.name}
        />
        <Title.Page styledAsLevel={1} style={{ paddingRight: '24rem' }}>
          {plot.name}
        </Title.Page>
        <Column style={{ paddingRight: '24rem' }}>
          {plot.byCrowdbuilding && daysLeft && daysLeft > 0 && (
            <Text.Large style={{ color: 'var(--palette-red-400)' }}>
              {translations.plotPage.applicationClosesInLabel.replace(
                '{0}',
                daysLeft.toString(),
              )}
            </Text.Large>
          )}
          <Subtitle>{plot.description}</Subtitle>
        </Column>
        <Row
          gap="1.5rem"
          style={{
            alignItems: 'flex-start',
            ...transformStyle,
          }}
        >
          <Column alignItems="stretch" gap="2rem" style={{ flex: '2 1 62rem' }}>
            <PageMediaGallery
              title={plot.name}
              images={plot.media}
              defaultImage={defaultProjectPicture}
              style={{ width: '100%' }}
            />
            <TabContent plot={plot} />
          </Column>
          <RightColumn plot={plot} />
        </Row>
      </Column>
    </PageLayout>
  )
}

const PageHead = ({ plot }: { plot: ViewPlotModel }) => {
  const appTranslations = useTranslations().app
  const translations = appTranslations.plotPage
  const { pageNames } = appTranslations
  const rootDomain = useRootDomain()
  const [tab] = useParamState(plotTabs)
  const tabTitle = match(tab)
    .with(plotTabs.overview, () => '')
    .with(plotTabs.application, () => translations.applicationProcedure)
    .with(plotTabs.documents, () => translations.documents)
    .with(plotTabs.thread, () => translations.thread)
    .exhaustive()
  const plotUrl = `${rootDomain}${pages.plot(plot.slug)}`
  const breadcrumbJsonLd = buildBreadcrumbListJsonLd([
    { name: pageNames.home, url: `${rootDomain}${pages.home}` },
    { name: pageNames.plots, url: `${rootDomain}${pages.plots}` },
    { name: plot.name, url: plotUrl },
  ])
  return (
    <PageSeo
      pageTitle={plot.name}
      tabTitle={tabTitle}
      description={plot.description}
      meta={{
        metaTitle: plot.metaTitle,
        metaDescription: plot.metaDescription,
      }}
      image={plot.media?.[0]}
      jsonLd={breadcrumbJsonLd}
      noindex={shouldNoindexPageTab(tab)}
    />
  )
}
